import type { RefObject } from '@mui/x-internals/types';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_REORDER_COL_DEF,
  gridColumnFieldsSelector,
  gridDataRowIdsSelector,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  gridSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
} from '@mui/x-data-grid-pro';
import type { GridRowId, GridRowTreeConfig } from '@mui/x-data-grid-pro';
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  isGroupingColumn,
  createSelectorMemoized,
  gridPinnedRowsSelector,
} from '@mui/x-data-grid-pro/internals';
import type { GridPrivateApiPremium } from '../../../models/gridApiPremium';
import type { GridStatePremium } from '../../../models/gridStatePremium';
import { columnIndexToLetters } from './engine';
import type { FormulaPositionContext } from './engine';

/**
 * Field of the autogenerated row-number column shown in A1 notation mode. It is
 * a utility column: excluded from the position context (takes no column letter),
 * from export/print, and from formulas.
 */
export const GRID_FORMULA_ROW_NUMBER_FIELD = '__formula_row_number__';

/**
 * Context for formulas without position-dependent syntax — their binding and
 * evaluation never consult positions, so building a real snapshot for them
 * would be wasted work.
 */
export const EMPTY_POSITION_CONTEXT: FormulaPositionContext = {
  version: 0,
  rowCount: 0,
  columnCount: 0,
  dataFromIndex: 1,
  dataToIndex: 0,
  getRowIdAtPosition: () => undefined,
  getPositionOfRowId: () => undefined,
  getFieldAtPosition: () => undefined,
  getPositionOfField: () => undefined,
};

/**
 * The inputs a position context is built from: sorted + filtered data-row
 * order and visible-column order. Rebind events compare snapshots to skip
 * rebinding when nothing actually moved.
 */
export interface GridFormulaPositionSnapshot {
  /**
   * Every addressable row in view order: top-pinned rows, then the sorted and
   * filtered data rows, then bottom-pinned rows.
   */
  rowIds: GridRowId[];
  /**
   * 1-based bounds of the data band inside `rowIds` (see
   * `FormulaPositionContext.dataFromIndex`).
   */
  dataFromIndex: number;
  dataToIndex: number;
  fields: string[];
}

/**
 * Assembles the addressable row order from the data band and the pinned rows
 * around it, and records where the data band starts and ends. Pinned rows are
 * addressable (they take a row number and an A1 address) but never take part
 * in a `RANGE_REF` or `COLUMN_VALUES` — see `FormulaPositionContext.dataFromIndex`.
 */
function assemblePositionRowIds(
  dataIds: GridRowId[],
  pinnedRows: {
    top?: readonly { id: GridRowId }[];
    bottom?: readonly { id: GridRowId }[];
  },
): Pick<GridFormulaPositionSnapshot, 'rowIds' | 'dataFromIndex' | 'dataToIndex'> {
  const top = pinnedRows.top?.map((entry) => entry.id) ?? [];
  const bottom = pinnedRows.bottom?.map((entry) => entry.id) ?? [];
  return {
    rowIds: top.length === 0 && bottom.length === 0 ? dataIds : [...top, ...dataIds, ...bottom],
    dataFromIndex: top.length + 1,
    dataToIndex: top.length + dataIds.length,
  };
}

const NO_PINNED_ROWS = { top: [], bottom: [] };

/**
 * Only data rows take part in the data band: autogenerated grouping nodes,
 * footers and skeleton rows are excluded. Tree-data parents are real data rows
 * and keep a position.
 */
function collectPositionRowIds(
  sourceIds: readonly GridRowId[],
  tree: GridRowTreeConfig,
): GridRowId[] {
  const rowIds: GridRowId[] = [];
  for (const id of sourceIds) {
    const node = tree[id];
    if (
      node !== undefined &&
      (node.type === 'leaf' || (node.type === 'group' && !node.isAutoGenerated))
    ) {
      rowIds.push(id);
    }
  }
  return rowIds;
}

const UTILITY_FIELDS = new Set<string>([
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_REORDER_COL_DEF.field,
  GRID_TREE_DATA_GROUPING_FIELD,
  GRID_FORMULA_ROW_NUMBER_FIELD,
]);

/**
 * Mirror of the row-side exclusion on the column axis: utility columns the
 * grid generates (selection checkbox, detail panel toggle, row reorder
 * handle, grouping columns) hold no data and take no column position —
 * otherwise enabling `checkboxSelection` would shift every positional
 * column reference by one.
 */
export function isPositionedDataField(field: string): boolean {
  return !UTILITY_FIELDS.has(field) && !isGroupingColumn(field);
}

export function buildFormulaPositionSnapshot(
  apiRef: RefObject<GridPrivateApiPremium>,
): GridFormulaPositionSnapshot {
  const dataRowIds = gridDataRowIdsSelector(apiRef);
  // Mount window: filtering applies before the first `applySorting`, so
  // `sortedRows` can be empty while rows exist. Fall back to the row-tree
  // order the initial evaluation used — the snapshots compare equal and the
  // rebind waits for the mount `sortedRowsSet` instead of binding everything
  // against an empty view.
  const sourceIds =
    gridSortedRowIdsSelector(apiRef).length === 0 && dataRowIds.length > 0
      ? dataRowIds
      : gridFilteredSortedRowIdsSelector(apiRef);
  return {
    ...assemblePositionRowIds(
      collectPositionRowIds(sourceIds, gridRowTreeSelector(apiRef)),
      gridPinnedRowsSelector(apiRef),
    ),
    fields: gridVisibleColumnFieldsSelector(apiRef).filter(isPositionedDataField),
  };
}

/**
 * State-based variant for the initial evaluation: the sorting and filtering
 * states initialize after the formula state, so the initial snapshot uses
 * the row-tree order. The mount-time sorting/filtering cascade publishes
 * `sortedRowsSet`/`filteredRowsSet`, which rebind position-dependent
 * formulas against the real view order.
 */
export function buildFormulaPositionSnapshotFromState(
  state: Partial<GridStatePremium>,
): GridFormulaPositionSnapshot {
  const visibilityModel = state.columns?.columnVisibilityModel ?? {};
  return {
    ...assemblePositionRowIds(
      state.rows ? collectPositionRowIds(state.rows.dataRowIds, state.rows.tree) : [],
      state.rows?.additionalRowGroups?.pinnedRows ?? NO_PINNED_ROWS,
    ),
    fields: (state.columns?.orderedFields ?? []).filter(
      (field) => visibilityModel[field] !== false && isPositionedDataField(field),
    ),
  };
}

export function createFormulaPositionContext(
  snapshot: GridFormulaPositionSnapshot,
  version: number,
): FormulaPositionContext {
  const { rowIds, fields, dataFromIndex, dataToIndex } = snapshot;
  // The reverse map keys by stringified id, consistent with the engine
  // cell-key format and the grid's rows lookup — both coerce ids to strings.
  const rowIdToPosition = new Map<string, number>();
  rowIds.forEach((id, index) => rowIdToPosition.set(String(id), index + 1));
  const fieldToPosition = new Map<string, number>();
  fields.forEach((field, index) => fieldToPosition.set(field, index + 1));
  return {
    version,
    rowCount: rowIds.length,
    columnCount: fields.length,
    dataFromIndex,
    dataToIndex,
    getRowIdAtPosition: (index) => rowIds[index - 1],
    getPositionOfRowId: (id) => rowIdToPosition.get(String(id)),
    getFieldAtPosition: (index) => fields[index - 1],
    getPositionOfField: (field) => fieldToPosition.get(field),
  };
}

export function arePositionArraysEqual<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Live position context for the A1 UI and transforms, memoized over its inputs
 * so the whole paste batch and every header/row-number cell in one render share
 * a single snapshot. Its row positions are exactly what `ROW_POSITION(n)`/A1
 * resolve to (sorted + filtered order, autogenerated and pinned rows excluded,
 * pagination ignored) and its column positions exactly what the header letters
 * use, so the displayed numbers, letters and frozen references can never
 * disagree.
 *
 * Distinct from the evaluation cache's `positionContext`, which is built lazily
 * (only when position-dependent formulas exist) and replaced by rebind passes —
 * it can be `null` at edit time and lags the view by one pass.
 */
export const gridFormulaA1PositionContextSelector = createSelectorMemoized(
  gridDataRowIdsSelector,
  gridSortedRowIdsSelector,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  gridVisibleColumnFieldsSelector,
  gridPinnedRowsSelector,
  (
    dataRowIds,
    sortedRowIds,
    filteredSortedRowIds,
    tree,
    visibleFields,
    pinnedRows,
  ): FormulaPositionContext => {
    // Same mount-window fallback as `buildFormulaPositionSnapshot`.
    const sourceIds =
      sortedRowIds.length === 0 && dataRowIds.length > 0 ? dataRowIds : filteredSortedRowIds;
    return createFormulaPositionContext(
      {
        ...assemblePositionRowIds(collectPositionRowIds(sourceIds, tree), pinnedRows),
        fields: visibleFields.filter(isPositionedDataField),
      },
      0,
    );
  },
);

/**
 * The A1 column letter for a field, or `''` when the field has no position
 * (utility/grouping/hidden column).
 */
export function getFormulaColumnLetter(context: FormulaPositionContext, field: string): string {
  const position = context.getPositionOfField(field);
  return position === undefined ? '' : columnIndexToLetters(position);
}

/**
 * The data fields a formula can reference by name, in column order. Utility,
 * grouping and row-number columns are excluded, but HIDDEN columns are kept:
 * a same-row bare reference (`=price`) resolves against the full column lookup
 * regardless of visibility, so the editor autocomplete offers them too. (A1
 * column letters, in contrast, are visibility-gated through the position
 * context — a hidden column has no letter.)
 */
export const gridFormulaReferenceableFieldsSelector = createSelectorMemoized(
  gridColumnFieldsSelector,
  (fields): string[] => fields.filter(isPositionedDataField),
);
